import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from './common/header';
import FiltersPanel from './FiltersPanel';
import ResultsPanel from './ResultsPanel';
import apiService from '../services/apiService';
import { importarRodada } from '../utils/rodada';
import '../styles/dadosUSP.css';

const DadosUSPApp = () => {
    const [currentFilters, setCurrentFilters] = useState(null); // Filters user is currently exploring
    const [appliedFilters, setAppliedFilters] = useState(null); // Filters saved when "Aplicar filtros" is clicked
    const [results, setResults] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Processando análise...');
    const [isModifyingProjects, setIsModifyingProjects] = useState(false);
    const [error, setError] = useState(null);
    const [importedFilters, setImportedFilters] = useState(null);
    const [dataReferencia, setDataReferencia] = useState('');
    const [saveStatus, setSaveStatus] = useState(null); // { tipo: 'salvando' | 'salvo' | 'erro', mensagem }
    const [searchParams] = useSearchParams();
    const rodadaId = searchParams.get('rodada');

    useEffect(() => {
        if (!rodadaId) return;
        let cancelled = false;
        setLoadingMessage('Carregando rodada salva...');
        setIsLoading(true);
        setError(null);
        setResults(null);
        apiService.abrirRodada(rodadaId)
            .then(arquivo => {
                if (cancelled) return;
                const rodada = importarRodada(arquivo);
                setResults(rodada.resultados);
                setAppliedFilters(rodada.filtros);
                setImportedFilters(rodada.filtros || {});
                setDataReferencia(rodada.dataReferencia);
            })
            .catch(err => {
                if (!cancelled) setError(err.message || 'Não foi possível abrir a rodada.');
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => { cancelled = true; };
    }, [rodadaId]);

    const handleFiltersChange = (newFilters) => {
        // Update the current filters as user explores
        setCurrentFilters(newFilters);
    };

    const handleApplyFilters = async (filters) => {
        setLoadingMessage('Processando análise...');
        setIsLoading(true);
        setError(null);
        setResults(null);
        setSaveStatus(null);

        try {
            // Store applied filters for export
            setAppliedFilters(filters);

            // Convert frontend filter format to backend format
            const backendFilters = convertFiltersToBackendFormat(filters);

            const response = await apiService.runAnalysis(backendFilters);

            // Debug da resposta recebida
            if (response && typeof response === 'object') {
                Object.keys(response).forEach(key => {
                    const dataset = response[key];
                    if (dataset && typeof dataset === 'object') {
                        const trlCount = dataset.TRL?.listValue?.length || 0;
                        const crlCount = dataset.CRL?.listValue?.length || 0;
                        const dataCount = dataset.dados_filtrados?.length || 0;
                    }
                });
            }

            if (response.error) {
                setError(response.error);
            } else {
                setResults(response);
                salvarRodada(response, filters);
            }
        } catch (err) {
            setError(err.message || 'Erro ao processar análise');
            console.error('Falha na análise:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Salva automaticamente no histórico cada rodada executada por "Aplicar filtros"
    const salvarRodada = async (resultados, filtros) => {
        setSaveStatus({ tipo: 'salvando', mensagem: 'Salvando rodada...' });
        try {
            await apiService.salvarRodada({ resultados, filtros, dataReferencia });
            setSaveStatus({ tipo: 'salvo', mensagem: 'Rodada salva' });
        } catch (err) {
            setSaveStatus({ tipo: 'erro', mensagem: err.message || 'Não foi possível salvar a rodada.' });
        }
    };

    const handleImport = (importedData) => {
        const rodada = importarRodada(importedData);
        setResults(rodada.resultados);
        setAppliedFilters(rodada.filtros);
        setImportedFilters(rodada.filtros || {});
        setDataReferencia(rodada.dataReferencia);
        setError(null);
    };

    const handleDataUpdate = (updatedData) => {
        setResults(updatedData);
        setError(null);
    };

    const convertFiltersToBackendFormat = (frontendFilters) => {

        const backendFilters = {};

        // Convert innovation degree checkboxes to array
        const selectedInnovationDegrees = Object.entries(frontendFilters.innovationDegree || {})
            .filter(([_, selected]) => selected)
            .map(([level]) => parseInt(level));

        if (selectedInnovationDegrees.length > 0) {
            backendFilters.grauInovacao = selectedInnovationDegrees;
        }

        // Convert arrays (if they have selections)
        if (frontendFilters.typology?.length > 0) {
            backendFilters.tipologia = frontendFilters.typology;
        }

        if (frontendFilters.status?.length > 0) {
            backendFilters.status = frontendFilters.status;
        }

        if (frontendFilters.portfolio?.length > 0) {
            backendFilters.portfolio = frontendFilters.portfolio;
        }

        if (frontendFilters.technologicalInitiative?.length > 0) {
            backendFilters.iniciativaTecnologica = frontendFilters.technologicalInitiative;
        }

        if (frontendFilters.category?.length > 0) {
            backendFilters.categoria = frontendFilters.category;
        }

        // Add TRL/CRL ranges if they're different from defaults
        if (frontendFilters.trlInitial !== 1 || frontendFilters.trlFinal !== 9) {
            backendFilters.trlRange = {
                initial: frontendFilters.trlInitial,
                final: frontendFilters.trlFinal
            };
        }

        if (frontendFilters.crlInitial !== 1 || frontendFilters.crlFinal !== 9) {
            backendFilters.crlRange = {
                initial: frontendFilters.crlInitial,
                final: frontendFilters.crlFinal
            };
        }

        // Add date filters if selected
        if (frontendFilters.dataMin && frontendFilters.dataMin.trim()) {
            backendFilters.dataMin = frontendFilters.dataMin;
        }

        if (frontendFilters.dataMax && frontendFilters.dataMax.trim()) {
            backendFilters.dataMax = frontendFilters.dataMax;
        }

        // Add predictions filter (default false if not provided)
        backendFilters.previsoes = frontendFilters.previsoes || false;

        return backendFilters;
    };

    return (
        <div className="h-screen flex flex-col overflow-hidden">
            <Header />
            <div className="flex-1 bg-gray-50 overflow-hidden">
                <div className="h-full ms-5 me-5">
                    <div className="flex h-full">
                        <FiltersPanel
                            importedFilters={importedFilters}
                            onFiltersChange={handleFiltersChange}
                            onApplyFilters={handleApplyFilters}
                            isDisabled={isLoading || isModifyingProjects || saveStatus?.tipo === 'salvando'}
                            saveStatus={saveStatus}
                        />
                        <ResultsPanel
                            dataReferencia={dataReferencia}
                            setDataReferencia={setDataReferencia}
                            results={results}
                            isLoading={isLoading}
                            loadingMessage={loadingMessage}
                            isModifyingProjects={isModifyingProjects}
                            setIsModifyingProjects={setIsModifyingProjects}
                            error={error}
                            appliedFilters={appliedFilters}
                            onImport={handleImport}
                            onDataUpdate={handleDataUpdate}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DadosUSPApp;
